import { createFileRoute, Link } from "@tanstack/react-router";

export const Route = createFileRoute("/tarifs")({
  head: () => ({
    meta: [
      { title: "Tarifs soins énergétiques Toulouse | Rénergescence" },
      { name: "description", content: "Découvrez les tarifs des soins énergétiques, massages et accompagnements proposés par Caroline à Toulouse. Transparence et bienveillance." },
      { property: "og:title", content: "Tarifs des soins énergétiques — Rénergescence Toulouse" },
      { property: "og:description", content: "Tarifs clairs des soins énergétiques, massages et naturopathie proposés par Caroline à Toulouse." },
      { property: "og:url", content: "https://renergescence.com/tarifs" },
    ],
    links: [{ rel: "canonical", href: "https://renergescence.com/tarifs" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Soins énergétiques",
          provider: {
            "@type": "LocalBusiness",
            name: "Rénergescence",
            address: {
              "@type": "PostalAddress",
              streetAddress: "3 rue Ledru-Rollin",
              addressLocality: "Toulouse",
              postalCode: "31300",
              addressCountry: "FR",
            },
          },
          areaServed: { "@type": "City", name: "Toulouse" },
          offers: [
            { "@type": "Offer", name: "Soin magnétisme", price: "50", priceCurrency: "EUR" },
            { "@type": "Offer", name: "Soin énergétique global", price: "60", priceCurrency: "EUR" },
            { "@type": "Offer", name: "Séance de naturopathie", price: "70", priceCurrency: "EUR" },
            { "@type": "Offer", name: "Massage Rénergescence 1h", price: "75", priceCurrency: "EUR" },
            { "@type": "Offer", name: "Massage sportif", price: "85", priceCurrency: "EUR" },
            { "@type": "Offer", name: "Soin Essentiel", price: "90", priceCurrency: "EUR" },
            { "@type": "Offer", name: "Massage Rénergescence 1h30", price: "110", priceCurrency: "EUR" },
          ],
        }),
      },
    ],
  }),
  component: Page,
});

type Service = { name: string; duration: string; price: string; desc: string };

const services: Service[] = [
  { name: "Soin magnétisme", duration: "≈ 45 min · présentiel ou distance", price: "50 €", desc: "Apaisement et harmonisation par imposition des mains." },
  { name: "Soin énergétique global", duration: "≈ 1h · présentiel ou distance", price: "60 €", desc: "Rééquilibrage profond des centres énergétiques et libération des blocages." },
  { name: "Séance de naturopathie", duration: "≈ 1h / 1h30", price: "70 €", desc: "Bilan complet et programme d'hygiène de vie personnalisé." },
  { name: "Massage Rénergescence", duration: "1h", price: "75 €", desc: "Massage relaxant et énergétique pour relâcher le corps et l'esprit." },
  { name: "Massage femme enceinte", duration: "1h", price: "70 €", desc: "Massage doux adapté au second et troisième trimestre." },
  { name: "Massage sportif", duration: "1h", price: "85 €", desc: "Récupération et décontraction musculaire profonde." },
  { name: "Soin Essentiel", duration: "1h massage + soin énergétique", price: "90 €", desc: "Combinaison signature massage + soin énergétique." },
  { name: "Massage Rénergescence", duration: "1h30", price: "110 €", desc: "Format long pour un relâchement complet et durable." },
];

const faqs = [
  { q: "Combien coûte un soin énergétique ?", a: "À Toulouse, un soin énergétique chez Rénergescence se situe entre 50 € (soin magnétisme) et 60 € (soin énergétique global). Le format Soin Essentiel, qui combine massage et soin énergétique, est à 90 €." },
  { q: "Le soin énergétique est-il remboursé ?", a: "Les soins énergétiques ne sont pas remboursés par la Sécurité sociale. En revanche, certaines mutuelles proposent un forfait médecines douces : renseignez-vous auprès de la vôtre." },
  { q: "Dois-je prévoir plusieurs séances ?", a: "Une seule séance apporte déjà des effets perceptibles. Selon votre situation, Caroline pourra vous conseiller un accompagnement sur 2 à 4 séances espacées de quelques semaines." },
  { q: "Puis-je offrir un soin en cadeau ?", a: "Oui, des bons cadeaux sont disponibles pour toutes les prestations, valables 6 mois. Vous pouvez les commander directement depuis la page Prestations." },
];

function Page() {
  return (
    <>
      <section className="pt-20 pb-10 md:pt-28">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <p className="eyebrow">Tarifs · Toulouse</p>
          <h1 className="mt-5 font-display text-4xl font-light italic md:text-6xl">
            Tarifs des soins énergétiques — Rénergescence Toulouse
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed md:text-lg" style={{ color: "var(--color-muted-foreground)" }}>
            Caroline vous propose des tarifs clairs et transparents, à la mesure de la qualité d'écoute et du temps consacré à chaque séance. Chaque prestation est pensée comme un véritable temps pour soi : accueil, soin et échanges sont compris dans le tarif. Aucun supplément, aucun engagement.
          </p>
        </div>
      </section>

      <section className="pb-16">
        <div className="mx-auto max-w-[1100px] px-6">
          <div className="grid gap-5 md:grid-cols-2">
            {services.map((s) => (
              <article key={s.name + s.duration} className="rounded-3xl border bg-white p-6 md:p-7" style={{ borderColor: "#e8e0d8" }}>
                <div className="flex items-baseline justify-between gap-4">
                  <h2 className="font-display text-xl" style={{ color: "#2D2B28" }}>{s.name}</h2>
                  <span className="font-display text-2xl" style={{ color: "var(--color-sage-text)" }}>{s.price}</span>
                </div>
                <p className="mt-1 text-xs uppercase tracking-[0.18em]" style={{ color: "#6a6258" }}>{s.duration}</p>
                <p className="mt-3 text-[14px] leading-relaxed" style={{ color: "var(--color-text)" }}>{s.desc}</p>
                <Link to="/contact" className="mt-5 inline-block rounded-full px-5 py-2 text-xs font-medium uppercase tracking-[0.14em] text-white" style={{ backgroundColor: "#7A8A76" }}>
                  Prendre rendez-vous
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16" style={{ backgroundColor: "var(--color-surface)" }}>
        <div className="mx-auto max-w-[820px] px-6">
          <h2 className="text-center font-display text-3xl font-light md:text-4xl">Ce qui est inclus</h2>
          <ul className="mt-10 grid gap-4 md:grid-cols-2">
            {[
              "Un temps d'accueil et d'écoute pour cerner vos besoins du jour",
              "Le soin lui-même, ajusté à vos besoins et à votre histoire",
              "Un échange post-séance pour intégrer les ressentis",
              "Des conseils personnalisés à mettre en place chez vous",
              "Une eau filtrée et un espace calme et apaisé",
              "Aucune obligation de suivi : chaque séance se suffit à elle-même",
            ].map((t) => (
              <li key={t} className="flex items-start gap-3 rounded-2xl border bg-white p-4" style={{ borderColor: "#e8e0d8" }}>
                <span className="mt-1 inline-block h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: "#7A8A76" }} />
                <span className="text-[15px] leading-relaxed" style={{ color: "var(--color-text)" }}>{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="py-16">
        <div className="mx-auto max-w-[820px] px-6">
          <h2 className="text-center font-display text-3xl font-light md:text-4xl">Questions fréquentes sur les tarifs</h2>
          <dl className="mt-10 space-y-5">
            {faqs.map((f) => (
              <div key={f.q} className="rounded-2xl border bg-white p-6" style={{ borderColor: "#e8e0d8" }}>
                <dt className="font-display text-lg" style={{ color: "#2D2B28" }}>{f.q}</dt>
                <dd className="mt-2 text-[15px] leading-relaxed" style={{ color: "var(--color-text)" }}>{f.a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <section className="pb-20">
        <div className="mx-auto max-w-2xl px-6 text-center">
          <p className="text-sm" style={{ color: "var(--color-muted-foreground)" }}>
            Modes de paiement : espèces et virement bancaire. Annulation possible jusqu'à 48&nbsp;h avant le rendez-vous.
          </p>
          <Link to="/contact" className="mt-8 inline-block rounded-full px-8 py-4 text-sm font-medium text-white" style={{ backgroundColor: "#7A8A76" }}>
            Prendre rendez-vous
          </Link>
        </div>
      </section>
    </>
  );
}